import axios from 'axios';
import router from '@/router';
import { ElMessage, ElMessageBox } from 'element-plus';
// 导入Token操作方法（医生Token + 学生全局Token）
import { getToken, removeToken, getGlobalToken, removeGlobalToken, setGlobalToken } from '@/utils/auth';

// 1. 从环境变量优先获取 baseURL，兼容硬编码 fallback https://xlfk.duvvv.cn/api1 http://localhost:8056 http://121.196.244.199:8056
const baseURL = 'https://xlfk.duvvv.cn/api1';


// 2. 不需要携带 token 的接口路径（Set 优化查找）
const NO_TOKEN_PATHS = new Set([
    '/api/schoolInfos/all', // 获取学校列表接口无需Token（首次获取全局Token）
    '/api/physicians/checkLogin' // 医生登录接口无需Token（登录后才生成Token）
]);

// 3. 标记需要携带学生全局Token的接口路径
const GLOBAL_TOKEN_PATHS = new Set([
    '/api/questionnaires/getSfzAndHopId',
    '/api/questionnaires/addOrUpdate',
    '/api/questionnaires/uploadParentSign',
    '/api/questionnaires/uploadProof',
    '/api/questionnaires/diseaseTypes',
    '/api/review/add',
    '/api/review/update',
    '/api/review/generateProof'
]);

// 4. 标记需要携带医生Token的接口路径
const DOCTOR_TOKEN_PATHS = new Set([
    '/api/physicians/checkLogin',
    '/api/physicians/uploadSignature',
    '/api/review/listAll',
    '/api/review/listByChildrenReview',
    '/api/review/exportToExcel',
    '/api/review/updateDoctor'
]);

// 5. 创建 axios 实例
const service = axios.create({
    baseURL,
    timeout: 30000, // 延长超时时间（导出文件可能耗时较长）
    withCredentials: false,
    headers: {
        'Content-Type': 'application/json;charset=utf-8'
    }

});

// 6. 请求拦截器：差异化携带Token + 自动拼接hopId
service.interceptors.request.use(
    (config) => {
        // 自动拼接URL中的hopId到请求参数
        const urlParams = new URLSearchParams(window.location.search);
        const hopId = urlParams.get('hopId');
        if (hopId) {
            config.params = { ...config.params, hopId };
        }

        // 跳过Token的接口，直接返回
        if (NO_TOKEN_PATHS.has(config.url)) {
            return config;
        }

        // 携带学生全局Token的接口
        if (GLOBAL_TOKEN_PATHS.has(config.url) || config.meta?.tokenType === 'global') {
            const globalToken = getGlobalToken();
            if (globalToken) {
                config.headers['Authorization'] = `Bearer ${globalToken}`;
                config.headers['Token-Type'] = 'global'; // 告诉后端是学生全局Token
            } else {
                ElMessage.warning('学生认证Token缺失，请刷新页面');
            }
            return config;
        }

        // 携带医生Token的接口
        if (DOCTOR_TOKEN_PATHS.has(config.url) || config.meta?.tokenType === 'doctor') {
            const doctorToken = getToken();
            if (doctorToken) {
                config.headers['Authorization'] = `Bearer ${doctorToken}`;
                config.headers['Token-Type'] = 'doctor'; // 告诉后端是医生Token
            } else {
                ElMessage.warning('医生登录Token缺失，请重新登录');
            }
            return config;
        }

        // 其他接口默认携带本地存储的userToken（兼容原有逻辑）
        const token = localStorage.getItem('userToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        // FormData请求自动处理Content-Type
        if (config.data instanceof FormData) {
            config.headers['Content-Type'] = undefined; // 让浏览器自动设置
        }

        return config;
    },
    (error) => {
        console.error('【请求拦截器错误】:', error);
        ElMessage.error('请求发送失败，请检查网络');
        return Promise.reject(error);
    }
);

// 7. 响应拦截器：
// - 从学校接口message提取并存储全局Token
// - 根据Token类型处理失效逻辑
// - 兼容文件流和普通JSON响应
service.interceptors.response.use(
    (response) => {
        // 情况1：处理学校列表接口，提取message中的全局Token并存储
        if (response.config.url === '/api/schoolInfos/all' && response.data?.message) {
            const globalToken = response.data.message;
            setGlobalToken(globalToken); // 存储到localStorage
            localStorage.setItem('userToken', globalToken); // 兼容原有userToken逻辑
            console.log('从学校接口获取并存储学生全局Token：', globalToken);
        }

        // 情况2：responseType 为 blob（文件流），返回完整响应对象
        if (response.config.responseType === 'blob') {
            return response;
        }

        // 情况3：普通接口（JSON 响应），返回 response.data
        const res = response.data;
        if (res?.code !== 200 && res?.success === false) {
            ElMessage.error(res.message || '操作失败');
            return Promise.reject(res);
        }
        return res;
    },
    (error) => {
        console.error('【响应拦截器错误】:', error);
        let errorMessage = '请求失败，请稍后重试';

        if (error.response) {
            const status = error.response.status;
            const resData = error.response.data;
            const tokenType = error.config.headers['Token-Type']; // 获取请求的Token类型

            switch (status) {
                case 401:
                    // 学生全局Token失效
                    if (tokenType === 'global') {
                        errorMessage = '学生认证已过期，请重新加载页面';
                        removeGlobalToken();
                        localStorage.removeItem('userToken');
                        ElMessageBox.confirm(
                            '学生认证已过期，是否重新加载页面获取Token？',
                            'Token失效',
                            {
                                confirmButtonText: '重新加载',
                                cancelButtonText: '取消',
                                type: 'warning'
                            }
                        ).then(() => {
                            window.location.reload();
                        });
                    }
                    // 医生Token失效
                    else if (tokenType === 'doctor') {
                        errorMessage = '医生登录已过期，请重新登录';
                        removeToken();
                        localStorage.removeItem('userToken');
                        localStorage.removeItem('userInfo');
                        // 避免重复跳转登录页
                        if (router.currentRoute.path !== '/denglu' && router.currentRoute.path !== '/') {
                            router.push({
                                path: '/',
                                query: { redirect: router.currentRoute.fullPath }
                            });
                        }
                    }
                    // 通用Token失效
                    else {
                        errorMessage = '登录已过期，请重新操作';
                        localStorage.removeItem('userToken');
                    }
                    break;
                case 403:
                    errorMessage = '您没有权限执行此操作';
                    break;
                case 404:
                    errorMessage = '请求的接口不存在';
                    break;
                case 500:
                    errorMessage = '服务器内部错误，请联系管理员';
                    break;
                default:
                    // 尝试解析 blob 类型的错误信息（导出失败时可能返回）
                    if (resData instanceof Blob) {
                        return new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                                try {
                                    const errObj = JSON.parse(e.target.result);
                                    errorMessage = errObj.message || '操作失败';
                                } catch (err) {
                                    errorMessage = '服务器返回未知错误';
                                }
                                ElMessage.error(errorMessage);
                                reject(new Error(errorMessage));
                            };
                            reader.readAsText(resData);
                        });
                    } else if (resData?.message) {
                        errorMessage = resData.message;
                    }
            }
        } else if (error.request) {
            // 无响应（网络错误）
            errorMessage = '网络异常，请检查连接';
        } else {
            errorMessage = `请求设置错误: ${error.message}`;
        }

        // 避免重复弹框（文件流错误已在内部处理）
        if (!(error.response?.data instanceof Blob)) {
            ElMessage.error(errorMessage);
        }
        return Promise.reject(new Error(errorMessage));
    }
);

// 导出请求函数，兼容不同请求方法
export default function request(config) {
    // 判断请求方法是否存在，默认使用 'get'
    const method = config.method?.toLowerCase() || 'get';

    // 根据请求方法调用对应axios方法
    if (method === 'get' || method === 'delete') {
        return service[method](config.url, { params: config.params, ...config });
    } else {
        return service[method](config.url, config.data, config);
    }
}